import {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useAppSelector} from '../../hooks/store';
import {usePrevious} from '../../hooks/usePrevious';
import {
  dispatchEmployeeSelect,
  dispatchStartTimeSelect,
} from '../../store/slices/dispatch';
import {
  selectEmployees,
  selectSelectedEmployee,
  selectSelectedStartTime,
  selectTimeslots,
} from '../../store/slices/selectors';
import {getSelectedStartTime} from '../CalendarNavigation/getSelectedStartTime';
import {
  trackOpenEmployeeList,
  trackSelectEmployee,
} from '../../utils/gtmTracking';
import {ANY_EMPLOYEE} from '../../store/slices/constants';
import {EmployeeSelect} from '../EmployeeSelect/EmployeeSelect';

export const EmployeeSelectContainer = () => {
  const employees = useAppSelector(selectEmployees);
  const selectedEmployee = useAppSelector(selectSelectedEmployee);
  const previousSelectedEmployee = usePrevious(selectedEmployee);
  const timeslots = useAppSelector(selectTimeslots);
  const selectedStartTime = useAppSelector(selectSelectedStartTime);

  // when the user chooses a different employee, the start time they have
  // selected may not be available on the same day. therefore we calculate
  // the available start time, and if that differs from the selected start time
  // in the store, we dispatch an action so that the store is updated with
  // the available start time for the current employee
  useEffect(() => {
    if (
      previousSelectedEmployee === undefined ||
      previousSelectedEmployee === selectedEmployee ||
      selectedStartTime === null
    ) {
      return;
    }

    const selectedTimeslot = getSelectedStartTime({
      selectedDayTimeSlots: timeslots,
      time: selectedStartTime,
    });

    const availableStartTime =
      selectedTimeslot !== null ? selectedTimeslot.startTime : null;

    if (
      availableStartTime &&
      selectedTimeslot &&
      availableStartTime !== selectedStartTime
    ) {
      dispatchStartTimeSelect(selectedTimeslot);
    }
  }, [
    previousSelectedEmployee,
    selectedEmployee,
    selectedStartTime,
    timeslots,
  ]);

  const handleDropdownToggle = useCallback((expanded: boolean) => {
    if (expanded) {
      trackOpenEmployeeList();
    }
  }, []);

  const handleSelectedEmployee = useCallback((value: string) => {
    trackSelectEmployee(value);
    dispatchEmployeeSelect(value);
  }, []);
  const {t} = useTranslation();
  const anyEmployeeLabel = t('calendar.anyEmployee');

  const employeeSelect = (
    <EmployeeSelect
      employeeItems={employees}
      labelId="employee-select-label"
      onSelected={handleSelectedEmployee}
      anyEmployeeLabel={anyEmployeeLabel}
      onToggle={handleDropdownToggle}
      initialSelectedEmployee={
        selectedEmployee !== ANY_EMPLOYEE ? selectedEmployee : undefined
      }
    />
  );

  return employeeSelect;
};
